import type { MaybeRefOrGetter } from "vue";

/**
 *
 * @param target - in addition to refs and html elements, this also accepts an element's id.
 * @param options - optional
 * @param {number} options.offset - number
 * @param options.offsetElement - in addition to refs and html elements, this also accepts an element's id.
 */
export const scrollToTarget = (
  target: MaybeRefOrGetter<HTMLElement | SVGElement | null | undefined> | string,
  options: {
    offset?: number;
    offsetElement?: MaybeRefOrGetter<HTMLElement | SVGElement | null | undefined> | string;
  } = {}
) => {
  const _target = toValue(target);

  const targetElement = typeof _target == "string" ? document.getElementById(_target) : _target;

  // Getting the top of the target element, or logging console error
  let targetElemTop: number;
  try {
    targetElemTop = targetElement!.getBoundingClientRect().top;
  } catch {
    console.error("invalid scroll to target");
    return;
  }

  let finalOffset: number = 0;
  if (options.offsetElement) {
    const _offsetTarget = toValue(options.offsetElement);

    const offsetTargetElement =
      typeof _offsetTarget == "string" ? document.getElementById(_offsetTarget) : _offsetTarget;

    finalOffset += offsetTargetElement ? offsetTargetElement.getBoundingClientRect().bottom : 0;
  }
  if (options.offset) finalOffset += options.offset;

  const displacement = targetElemTop - finalOffset;
  if (window) {
    window.scrollBy({
      top: displacement,
      behavior: "smooth"
    });
  }
};

export const FALLBACK_COUNTRY_LOCALE = "en-US";

/**
 * Picks a locale that is safe to hand to Intl for country names.
 *
 * Some browser/OS combinations report a language that is not a valid BCP 47
 * tag - Chrome on Windows with a custom region format reports values such as
 * "united states-UNITED STATES". Passing one of those to Intl.DisplayNames
 * throws a RangeError, so each candidate is tried against the constructor that
 * would throw and anything unusable falls back to English.
 *
 * @param candidates - language tags, most preferred first.
 */
export const resolveCountryLocale = (candidates: readonly (string | undefined)[]): string => {
  for (const tag of candidates) {
    if (!tag) continue;

    try {
      new Intl.DisplayNames([tag], { type: "region" });
      return tag;
    } catch {
      // malformed tag reported by the browser, try the next candidate
    }
  }

  return FALLBACK_COUNTRY_LOCALE;
};

export const isExternalUrl = (href: string): boolean => {
  if (!/^https?:\/\//i.test(href)) return false;
  try {
    const { hostname } = new URL(href);
    return hostname !== "asuonline.asu.edu" && !hostname.endsWith(".asuonline.asu.edu");
  } catch {
    return false;
  }
};

export const openExternalLinksInNewTab = (root: HTMLElement): void => {
  root.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((link) => {
    if (!isExternalUrl(link.getAttribute("href") ?? "")) return;
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });
};

/**
 * Redefines an object's property as a non-enumerable accessor, keeping it fully
 * readable and writable while hiding it from `Object.keys` and `JSON.stringify`.
 *
 * Vue records internal references on shared DOM nodes with plain assignments,
 * which makes them enumerable:
 *   - `document.body._lpa`  Teleport's "last placeholder anchor"
 *   - `#__nuxt._vnode`      the render container's root vnode
 *
 * `vnode.component.vnode` is circular, so any third-party script that
 * `JSON.stringify()`s one of those nodes throws "Converting circular structure
 * to JSON". Hiding the keys breaks the reference chain without changing any
 * behaviour Vue depends on.
 */
export const hideFromSerialization = (node: object | null | undefined, key: string): void => {
  if (!node) return;

  let value = (node as Record<string, unknown>)[key];

  try {
    Object.defineProperty(node, key, {
      get: () => value,
      set: (next: unknown) => {
        value = next;
      },
      enumerable: false,
      configurable: true
    });
  } catch {
    // A non-configurable property cannot be redefined; leaving it as-is is safe.
  }
};
